import React from 'react';
import {
	Card,
	CardContent,
	Typography,
	makeStyles,
	Chip,
	Box
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
	card: {
		marginBottom: theme.spacing(2),
	},
	cardContent: {
		padding: theme.spacing(2),
		'& > * + *': { marginTop: theme.spacing(2) },
	},
	metricsBar: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1.5),
		alignItems: 'center',
		padding: theme.spacing(1.5, 2),
		backgroundColor: theme.palette.grey[50],
		borderRadius: theme.shape.borderRadius,
	},
	metricPill: {
		display: 'inline-flex',
		flexDirection: 'column',
		alignItems: 'center',
		minWidth: 80,
		padding: theme.spacing(0.5, 1),
	},
	metricValue: {
		fontSize: '1.125rem',
		fontWeight: 600,
		color: theme.palette.primary.main,
		lineHeight: 1.2,
	},
	metricLabel: {
		fontSize: '0.7rem',
		color: theme.palette.text.secondary,
		textTransform: 'uppercase',
		letterSpacing: '0.02em',
		marginTop: 2,
	},
	section: {
		marginTop: theme.spacing(1.5),
	},
	sectionTitle: {
		fontSize: '0.8rem',
		fontWeight: 600,
		color: theme.palette.text.secondary,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		marginBottom: theme.spacing(0.75),
	},
	chipRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
	},
	expertiseChip: {
		margin: 0,
	},
	summaryBox: {
		backgroundColor: theme.palette.grey[50],
		padding: theme.spacing(1.5, 2),
		borderRadius: theme.shape.borderRadius,
		borderLeft: `3px solid ${theme.palette.primary.main}`,
	},
	summaryBoxFirst: {
		marginRight: theme.spacing(1),
	},
	summaryBoxSecond: {
		marginLeft: theme.spacing(1),
	},
	summaryTitle: {
		fontSize: '0.8rem',
		fontWeight: 600,
		marginBottom: theme.spacing(0.5),
	},
	summaryText: {
		fontSize: '0.875rem',
		lineHeight: 1.5,
		color: theme.palette.text.secondary,
	},
	twoCol: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: theme.spacing(2),
		columnGap: theme.spacing(4),
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: '1fr',
			columnGap: 0,
			'& $summaryBoxFirst': { marginRight: 0 },
			'& $summaryBoxSecond': { marginLeft: 0 },
		},
	},
}));

const AIAnalysis = ({ aiAnalysis }) => {
	const classes = useStyles();

	if (!aiAnalysis) {
		return null;
	}

	return (
		<Card className={classes.card}>
			<CardContent className={classes.cardContent}>
				<Typography variant="h6" style={{ marginBottom: 8 }}>
					AI Analysis
				</Typography>

				{/* Compact metrics bar */}
				<div className={classes.metricsBar}>
					<div className={classes.metricPill}>
						<Typography className={classes.metricValue}>{aiAnalysis.difficulty ?? 0}/10</Typography>
						<Typography className={classes.metricLabel}>Difficulty</Typography>
					</div>
					<div className={classes.metricPill}>
						<Typography className={classes.metricValue}>{aiAnalysis.estimatedTimePer1000Words ?? 0}h</Typography>
						<Typography className={classes.metricLabel}>Per 1000 words</Typography>
					</div>
					<div className={classes.metricPill}>
						<Typography className={classes.metricValue}>{aiAnalysis.detectedLanguage || '—'}</Typography>
						<Typography className={classes.metricLabel}>Language</Typography>
					</div>
					<div className={classes.metricPill}>
						<Typography className={classes.metricValue}>{aiAnalysis.contentType || '—'}</Typography>
						<Typography className={classes.metricLabel}>Content type</Typography>
					</div>
				</div>

				{/* Expertise & challenges in one row on larger screens */}
				{(aiAnalysis.requiredExpertise?.length > 0 || aiAnalysis.keyChallenges?.length > 0) && (
					<Box display="flex" flexWrap="wrap" style={{ gap: 24 }}>
						{aiAnalysis.requiredExpertise && aiAnalysis.requiredExpertise.length > 0 && (
							<div className={classes.section} style={{ flex: '1 1 200px' }}>
								<Typography className={classes.sectionTitle}>Required expertise</Typography>
								<div className={classes.chipRow}>
									{aiAnalysis.requiredExpertise.map((expertise, index) => (
										<Chip
											key={index}
											label={expertise}
											color="primary"
											size="small"
											className={classes.expertiseChip}
										/>
									))}
								</div>
							</div>
						)}
						{aiAnalysis.keyChallenges && aiAnalysis.keyChallenges.length > 0 && (
							<div className={classes.section} style={{ flex: '1 1 200px' }}>
								<Typography className={classes.sectionTitle}>Key challenges</Typography>
								<div className={classes.chipRow}>
									{aiAnalysis.keyChallenges.map((challenge, index) => (
										<Chip
											key={index}
											label={challenge}
											size="small"
											variant="outlined"
											className={classes.expertiseChip}
										/>
									))}
								</div>
							</div>
						)}
					</Box>
				)}

				{/* Summary & approach side-by-side on larger screens */}
				{(aiAnalysis.summary || aiAnalysis.recommendedApproach) && (
					<div className={classes.twoCol}>
						{aiAnalysis.summary && (
							<Box className={`${classes.summaryBox} ${classes.summaryBoxFirst}`}>
								<Typography className={classes.summaryTitle}>AI Summary</Typography>
								<Typography className={classes.summaryText}>{aiAnalysis.summary}</Typography>
							</Box>
						)}
						{aiAnalysis.recommendedApproach && (
							<Box className={`${classes.summaryBox} ${classes.summaryBoxSecond}`}>
								<Typography className={classes.summaryTitle}>Recommended approach</Typography>
								<Typography className={classes.summaryText}>{aiAnalysis.recommendedApproach}</Typography>
							</Box>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
};

export default AIAnalysis;
